import React from 'react'
import styled from '@emotion/styled'
import { colors, Checkbox } from 'ic-snacks'
import moment, { type Moment } from 'moment'
import { DayPickerRangeController, type FocusedInputShape } from 'react-dates'
import { START_DATE, END_DATE } from 'react-dates/constants'
import { FormattedMessage, FormattedDate } from 'react-intl'
import OutsideClickHandler from 'react-outside-click-handler'
import ButtonBase from 'legacy/components/ButtonBase'
import Card from 'legacy/components/Card/Card'
import { MiniHeader, TileSection } from './Styles'

const DatesTileSection = styled(TileSection)`
  flex-direction: column;
  align-items: stretch;
`

const CalendarContainer = styled(Card)`
  position: absolute;
  z-index: 2;
  top: 250px;
  left: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: center;

  .CalendarDay {
    font-size: 11px;
  }

  .DayPickerNavigation_leftButton__horizontalDefault {
    left: 21px;
  }

  .DayPickerNavigation_rightButton__horizontalDefault {
    right: 21px;
  }

  .CalendarMonth_caption {
    font-size: 13px;
    font-weight: 500;
    padding-top: 25px;
    padding-bottom: 34px;
  }

  .DayPicker_weekHeader_ul {
    font-size: 10px;
  }

  .DayPickerNavigation_button__horizontal {
    padding: 5px 8px;
  }

  .DayPickerNavigation_svg__horizontal {
    width: 16px;
    height: 16px;
  }
`

const DatesContainer = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
`

type DateButtonProps = {
  active: boolean
} & React.ComponentPropsWithoutRef<typeof ButtonBase>

const DateButton = styled<React.FunctionComponent<DateButtonProps>>(({ active, ...rest }) => (
  <ButtonBase {...rest} />
))`
  flex: 0 0 50%;
  padding: 12px 8px;
  border-left: 1px solid ${colors.GRAY_88};
  text-align: left;

  &:first-child {
    border-left: none;
  }

  ${MiniHeader} {
    color: ${({ active }) => (active ? colors.GREEN_500 : colors.GRAY_74)};
  }

  &:hover {
    ${MiniHeader} {
      color: ${colors.GREEN_500};
    }
  }
`

const Date = styled.span<{ selected?: boolean }>`
  display: block;
  font-size: 14px;
  font-weight: 200;
  color: ${({ selected }) => (selected ? colors.GRAY_20 : colors.GRAY_74)};
`

interface Props {
  startDate: Moment | null
  endDate: Moment | null
  onChange(values: { startDate: Moment | null; endDate: Moment | null }): void
  onOpen?(): void
  onClose?(): void
}

interface State {
  openCalendar: FocusedInputShape | null
  checkedNoEndDate: boolean
}

const dateTypes: Array<FocusedInputShape> = [START_DATE, END_DATE]

class DepartmentDates extends React.Component<Props, State> {
  static getDerivedStateFromProps(props: Props, prevState: State) {
    const { startDate, endDate } = props
    const { checkedNoEndDate, openCalendar } = prevState

    if (checkedNoEndDate && (endDate || (!startDate && !openCalendar))) {
      return { checkedNoEndDate: false }
    }

    if (!checkedNoEndDate && startDate && !endDate && !openCalendar) {
      return { checkedNoEndDate: true }
    }

    return null
  }

  state: State = {
    openCalendar: null,
    checkedNoEndDate: Boolean(this.props.startDate && !this.props.endDate),
  }

  componentDidUpdate(_: Props, prevState: State) {
    const { onOpen, onClose } = this.props

    if (!prevState.openCalendar && this.state.openCalendar) {
      onOpen && onOpen()
    } else if (prevState.openCalendar && !this.state.openCalendar) {
      onClose && onClose()
    }
  }

  handleClickDate(type: FocusedInputShape | null) {
    this.setState({ openCalendar: type })
  }

  handleFocusChange = (type: FocusedInputShape | null) => {
    this.setState({ openCalendar: type })
  }

  handleOutsideClick = () => {
    const { startDate, endDate, onChange } = this.props
    let { checkedNoEndDate } = this.state

    // If user clicks out with only endDate selected, auto select start date
    if (!startDate && (endDate || checkedNoEndDate)) {
      onChange({ startDate: moment(), endDate })
    }

    // If user clicks out with only start date selected, auto set "no end date"
    if (startDate !== null && endDate === null && !checkedNoEndDate) {
      checkedNoEndDate = true
    }

    this.setState({ openCalendar: null, checkedNoEndDate })
  }

  handleNoEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked: checkedNoEndDate } = e.target
    const { startDate, endDate } = this.props

    if (endDate && checkedNoEndDate) {
      this.props.onChange({ startDate, endDate: null })
    }

    const openCalendar = !startDate ? START_DATE : checkedNoEndDate ? null : END_DATE

    this.setState({
      checkedNoEndDate,
      openCalendar,
    })
  }

  handleChange = (values: { startDate: Moment | null; endDate: Moment | null }) => {
    let { endDate } = values
    const { startDate } = values
    const { openCalendar, checkedNoEndDate } = this.state

    // force endDate to be null if "no end date" box is checked as
    // react-dates may report not null due to the fake endDate
    // we pass for the visual representation of "no end date" on the calendar
    if (openCalendar === START_DATE && checkedNoEndDate) {
      endDate = null
    }

    this.props.onChange({ startDate, endDate })
  }

  renderDate(type: FocusedInputShape) {
    const { startDate, endDate } = this.props
    const { checkedNoEndDate } = this.state

    const date = type === START_DATE ? startDate : endDate

    if (!date && type === END_DATE && checkedNoEndDate) {
      return (
        <Date selected>
          <FormattedMessage id="common.none" />
        </Date>
      )
    }

    if (!date) {
      return <Date>—/—/—</Date>
    }

    return (
      <Date selected>
        <FormattedDate value={date.toDate()} />
      </Date>
    )
  }

  renderCalendar() {
    const { startDate, endDate } = this.props
    const { openCalendar, checkedNoEndDate } = this.state

    if (!openCalendar) {
      return null
    }

    const visualEndDate =
      checkedNoEndDate && startDate ? startDate.clone().add(10, 'years') : endDate

    return (
      <CalendarContainer depth={2} width="240px">
        <DayPickerRangeController
          noBorder
          hideKeyboardShortcutsPanel
          daySize={28}
          // yolo workaround for untyped prop
          {...{ horizontalMonthPadding: 12 }}
          minimumNights={0}
          startDate={startDate}
          endDate={visualEndDate}
          onDatesChange={this.handleChange}
          focusedInput={openCalendar}
          onFocusChange={this.handleFocusChange}
        />
        {openCalendar === END_DATE && (
          <Card.Footer dense style={{ fontSize: 11, color: colors.GRAY_20 }}>
            <Checkbox
              id="checkbox-dept-no-end-date"
              isSelected={checkedNoEndDate}
              onChange={this.handleNoEndDateChange}
            >
              <FormattedMessage id="common.dates.noEndDate" />
            </Checkbox>
          </Card.Footer>
        )}
      </CalendarContainer>
    )
  }

  render() {
    const { openCalendar } = this.state

    return (
      <DatesTileSection padding="0 8px" height="60px">
        <OutsideClickHandler
          display="flex"
          disabled={openCalendar === null}
          onOutsideClick={this.handleOutsideClick}
        >
          <DatesContainer>
            {dateTypes.map(dateType => (
              <DateButton
                key={dateType}
                active={openCalendar === dateType}
                onClick={() => this.handleClickDate(dateType)}
              >
                <MiniHeader>
                  <FormattedMessage id={`common.dates.${dateType}`} />
                </MiniHeader>
                {this.renderDate(dateType)}
              </DateButton>
            ))}
          </DatesContainer>
          {this.renderCalendar()}
        </OutsideClickHandler>
      </DatesTileSection>
    )
  }
}

export default DepartmentDates
